import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Chip, LinearProgress, Skeleton, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { AutoAwesome, CheckCircle, PlayArrowRounded } from '@mui/icons-material'
import { Link, useNavigate } from 'react-router-dom'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useAlert, useFormFactor, useTrackAiSkillsPracticeEvent } from '../../../../hooks'
import { aiSkillsPracticeSlice } from '../../../../reducers'
import { AI_SKILLS_PRACTICE_FROM } from '../../../../hooks/useTrackAiSkillsPracticeEvent'
import { HomeRails, WIDE_RAIL_SLOT_WIDTH } from '../home-rails'
import { ActionButton } from '../../../../components'
import { ScormPlayer } from '../../../../components/scorm-player/scorm-player'
import SkillBiteCardContainer from '../../../skill-bites/components/cards/skill-bite-card-container'
import { SkillBiteInfoContainer } from '../../../skill-bites/components/cards/skill-bite-card-info-container'
import { routes } from '../../../../router/routes'

const useStyles = makeStyles(({ spacing, palette, typography }) => ({
  image: {
    borderRadius: spacing(1),
    width: '100%',
    aspectRatio: '16 / 9',
    objectFit: 'cover'
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: spacing(1),
    background: 'linear-gradient(135deg, #7C3AED, #2563EB, #06B6D4)',
    flexShrink: 0
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    width: '100%',
    marginTop: spacing(1)
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    flex: 1
  },
  progressBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: palette.grey.shade[20],
    '& .MuiLinearProgress-bar': {
      borderRadius: 3
    }
  },
  progressText: {
    ...typography.body.s,
    color: palette.text.secondary,
    minWidth: 35,
    textAlign: 'right'
  }
}))

const AiSkillsPracticeCard = ({ item, dataTest, onNavigate, onLaunch, isLaunching, isLoadingProgress }) => {
  const classes = useStyles()
  const userProgress = item.userProgress

  const renderActions = () => {
    if (isLoadingProgress) {
      return (
        <Box className={classes.actionsContainer}>
          <Skeleton height={40} width={120} />
          <Skeleton height={6} sx={{ flex: 1 }} />
        </Box>
      )
    }

    if (userProgress?.status === 'completed') {
      return (
        <Box className={classes.actionsContainer}>
          <Chip
            label='Completed'
            icon={<CheckCircle />}
            sx={{
              borderRadius: '4px',
              backgroundColor: '#268731',
              color: '#FFF',
              height: '36px',
              '& .MuiSvgIcon-root': { color: '#FFF' }
            }}
            data-test={`${dataTest}-status-completed`}
            aria-label='Course completed'
          />
        </Box>
      )
    }

    const hasStarted = userProgress?.status === 'started'

    return (
      <Box className={classes.actionsContainer}>
        <ActionButton
          variant='neutral'
          label={hasStarted ? 'Resume' : 'Launch'}
          startIcon={<PlayArrowRounded />}
          loading={isLaunching}
          onClick={(e) => { e.stopPropagation(); onLaunch() }}
          data-test={`${dataTest}-launch-button`}
          sx={{ flexShrink: 0, '&.Mui-disabled': { bgcolor: 'grey.800', color: 'common.white' } }}
        />
        {hasStarted && (
          <Box className={classes.progressContainer}>
            <LinearProgress
              variant='determinate'
              value={userProgress.progress ?? 0}
              className={classes.progressBar}
              aria-label={`Course progress: ${userProgress.progress ?? 0} percent`}
              aria-valuenow={userProgress.progress ?? 0}
              aria-valuemin={0}
              aria-valuemax={100}
              role='progressbar'
              data-test={`${dataTest}-progress-bar`}
            />
            <Typography className={classes.progressText} data-test={`${dataTest}-progress-text`}>
              {userProgress.progress ?? 0}%
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <SkillBiteCardContainer onClick={onNavigate} dataTest={dataTest} variant='generic'>
      <Box>
        <img
          className={classes.image}
          src={item.image?.imgUrl}
          alt={item.image?.altText || item.title}
          data-test={`${dataTest}-image`}
        />
      </Box>
      <SkillBiteInfoContainer
        isLoading={false}
        name={item.title}
        description={item.description}
        variant='generic'
      />
      {renderActions()}
    </SkillBiteCardContainer>
  )
}

export const AiSkillsPracticeRails = ({ aiSkillsPracticeSection }) => {
  const classes = useStyles()
  const { items } = aiSkillsPracticeSection
  const { isMobile } = useFormFactor()
  const { trackLaunched } = useTrackAiSkillsPracticeEvent()
  const { enqueueMsg } = useAlert()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const modules = useSelector(aiSkillsPracticeSlice.selectors.selectAiSkillsPracticeModules)
  const [courseProgressLoading, setCourseProgressLoading] = useState({})

  useEffect(() => {
    if (items?.length > 0 && modules.length === 0) {
      dispatch(aiSkillsPracticeSlice.actions.setModules({ modules: items }))
    }
  }, [items, dispatch, modules.length])

  const [scormPlayerOpen, setScormPlayerOpen] = useState(false)
  const [scormLaunchLink, setScormLaunchLink] = useState(null)
  const [scormTitle, setScormTitle] = useState(null)
  const [launchingCourseId, setLaunchingCourseId] = useState(null)
  const [activeCourseId, setActiveCourseId] = useState(null)

  const [getSkillBuilderUserProgress] = useEvent([{
    eventName: 'getSkillBuilderUserProgress',
    onSuccess: (payload) => {
      dispatch(aiSkillsPracticeSlice.actions.setModuleProgress(payload))
      setCourseProgressLoading(prev => ({ ...prev, [payload.courseId]: false }))
    },
    onError: (err) => {
      setCourseProgressLoading(prev => ({ ...prev, [err?.courseId ?? activeCourseId]: false }))
    }
  }])

  const [getScormLaunchLink] = useEvent([{
    eventName: 'scormGetAccessLink',
    onSuccess: (payload) => {
      setLaunchingCourseId(null)
      setScormLaunchLink(payload.launchLink)
      setScormPlayerOpen(true)
    },
    onError: () => {
      setLaunchingCourseId(null)
      enqueueMsg('Unable to launch this course. Please try again.', 'error')
    }
  }])

  const handleNavigateToLandingPage = useCallback(() => {
    navigate(routes['ai-skills-practice'].path, { state: { from: AI_SKILLS_PRACTICE_FROM.HOME_RAIL } })
  }, [navigate])

  const handleLaunch = useCallback((item) => {
    const courseId = item.scormCourseId ?? item.pk
    trackLaunched(AI_SKILLS_PRACTICE_FROM.HOME_RAIL, { itemTitle: item.title })
    if (courseId) {
      setLaunchingCourseId(courseId)
      setActiveCourseId(courseId)
      setScormTitle(item.title)
      getScormLaunchLink.trigger({ courseId })
    }
  }, [getScormLaunchLink, trackLaunched])

  const handleScormClose = useCallback(() => {
    setScormPlayerOpen(false)
    setScormLaunchLink(null)
    setScormTitle(null)
    if (activeCourseId) {
      setCourseProgressLoading(prev => ({ ...prev, [activeCourseId]: true }))
      setTimeout(() => {
        getSkillBuilderUserProgress.trigger({ courseId: activeCourseId })
      }, 2000) // Leave space for the progress to be updated
      setActiveCourseId(null)
    }
  }, [activeCourseId, getSkillBuilderUserProgress])

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box className={classes.iconBox} aria-hidden='true'>
        <AutoAwesome sx={{ color: '#fff', fontSize: 18 }} />
      </Box>
      <Typography sx={{ fontWeight: 700 }}>AI Skills Practice</Typography>
    </Box>
  )

  return (
    <>
      <HomeRails
        title={title}
        items={modules.length > 0 ? modules : items}
        itemWidth={WIDE_RAIL_SLOT_WIDTH}
        dataTest='home-ai-skills-practice-rails'
        exploreMoreCta={(
          <Typography variant={!isMobile ? 'caption' : undefined}>
            Discover all <Link to={routes['ai-skills-practice'].path} state={{ from: AI_SKILLS_PRACTICE_FROM.HOME_RAIL }} data-test='home-ai-skills-practice-explore-link'>AI Skills Practice</Link>
          </Typography>
        )}
        renderItem={(item, itemIndex) => (
          <AiSkillsPracticeCard
            item={item}
            dataTest={`home-ai-skills-practice-${itemIndex}`}
            onNavigate={handleNavigateToLandingPage}
            onLaunch={() => handleLaunch(item)}
            isLaunching={launchingCourseId === (item.scormCourseId ?? item.pk)}
            isLoadingProgress={courseProgressLoading[item.scormCourseId ?? item.pk] ?? false}
          />
        )}
      />
      <ScormPlayer
        open={scormPlayerOpen}
        onClose={handleScormClose}
        url={scormLaunchLink}
        title={scormTitle}
      />
    </>
  )
}
